import * as THREE from 'three';
import Action from '../classes/Action';
import Engine from '../classes/Engine';
import { CustomEventTypes, SaveModificationTypes } from '../globals';
import ProjectSaver from '../classes/ProjectSaver';
import Scenario from '../classes/scenarioElements/Scenario';
import ARTracker from '../classes/ARTracker';
import {
	ActionDescription,
	ActionParameters
} from "../classes/api/APIDecorators";
import {RegisterActionWithAPI} from "../classes/api/Decorators";

@ActionDescription("Deletes the objects from the scene.")
@ActionParameters({objects: "Object3D[]", actionName: "string"})
@RegisterActionWithAPI("RemoveObjectAction")
export default class RemoveObjectAction extends Action
{
	constructor(objects: THREE.Object3D[], actionName: string | undefined = undefined)
	{
		if (!actionName) actionName = objects.length > 0 ? objects.length > 1 ? "Deleted Objects" : "Deleted " + objects[0].name : "";

		const parents: THREE.Object3D[] = [];
		const childIndices: number[] = [];

		for (const object of objects)
		{
			parents.push(object.parent!);
			childIndices.push(object.parent!.children.indexOf(object));
		}

		super([objects], [objects, parents, childIndices], actionName, [objects]);
	}

	do(objects: THREE.Object3D[]) : void
	{
		Engine.getInstance().getPickingManager().clearSelection(false);

		for (const object of objects)
		{
			//object may have already been removed due to its parent also taking part in this action. this is easier than filtering the list beforehand
			if(object.userData.removedFromScene) continue; 
			
			const objectGuid = object.userData.guid;
			if (object instanceof ARTracker)
			{
				const cps = Scenario.getCPS()!;
				cps.trackers = cps.trackers!.filter((t) => t !== objectGuid);
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
			}

			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Removed);
			Engine.getInstance().removeObject(object, true);
		}
		
		Engine.getInstance().getPickingManager().storeSelection();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
	}

	undo(objects: THREE.Object3D[], parents: THREE.Object3D[], childIndices: number[]) : void
	{
		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			const parent = parents[i];
			const childIndex = childIndices[i];

			if(object.parent !== parent)
			{
				ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.ReAdded);
				Engine.getInstance().addObject(object, parent, undefined, true);
				const currentChildIndex = parent.children.indexOf(object);
				if (currentChildIndex !== childIndex)
				{
					parent.children.splice(childIndex, 0, parent.children.splice(currentChildIndex, 1)[0]);
				}
			}

			Engine.getInstance().getPickingManager().selectObject(object);

			if (object instanceof ARTracker)
			{
				Scenario.getCPS()!.trackers!.push(object.userData.guid);
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
	}

	verify(objects: THREE.Object3D[]) : boolean
	{
		return objects.length > 0;
	}
}
